import React, { useState, useEffect, useCallback } from 'react';
import { 
  Database, Clock, Download, Trash2, RefreshCw, 
  HardDrive, Calendar, CheckCircle, AlertCircle,
  Settings, Play, FileArchive, RotateCcw, Shield,
  ChevronDown, X, Info
} from 'lucide-react';
import api from '../../services/api';
import { format, parseISO } from 'date-fns';
import { sq } from 'date-fns/locale';

const BackupsPage = () => {
  const [loading, setLoading] = useState(true);
  const [backups, setBackups] = useState([]);
  const [settings, setSettings] = useState(null);
  const [creating, setCreating] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState(null);
  const [newSettings, setNewSettings] = useState({
    hour: 17,
    minute: 0,
    retention_days: 30
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      const [backupsRes, settingsRes] = await Promise.all([
        api.get('/backups/list'),
        api.get('/backups/settings')
      ]);
      
      setBackups(backupsRes.data.backups || []);
      setSettings(settingsRes.data);
      setNewSettings({
        hour: settingsRes.data.next_run ? parseInt(settingsRes.data.next_run.split('T')[1].split(':')[0]) : 17,
        minute: 0,
        retention_days: settingsRes.data.retention_days || 30
      });
      
    } catch (error) {
      console.error('Error fetching backups:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '--';
    try {
      return format(parseISO(dateStr), 'dd MMM yyyy HH:mm', { locale: sq });
    } catch {
      return dateStr;
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '0 B';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
  };

  const handleCreateBackup = async () => {
    try {
      setCreating(true);
      await api.post('/backups/create');
      await fetchData();
    } catch (error) {
      console.error('Error creating backup:', error);
      alert('Gabim në krijimin e backup');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteBackup = async (backupId) => {
    if (!window.confirm('A jeni i sigurt që dëshironi të fshini këtë backup?')) return;
    
    try {
      await api.delete(`/backups/${backupId}`);
      await fetchData();
    } catch (error) {
      console.error('Error deleting backup:', error);
      alert('Gabim në fshirjen e backup');
    }
  };

  const handleDownloadBackup = async (backup) => {
    try {
      const response = await api.post(`/backups/${backup.id}/download`);
      
      // Open download URL
      window.open(response.data.download_url, '_blank');
    } catch (error) {
      console.error('Error downloading backup:', error);
      alert('Gabim në shkarkimin e backup');
    }
  };

  const handleDownloadFullDatabase = async (format = 'json') => {
    try {
      const response = await api.get(`/backups/download-full?format=${format}`, {
        responseType: 'blob'
      });
      
      const contentType = response.headers['content-type'];
      const extension = format === 'json' ? 'json' : 'zip';
      const filename = `tiataxi_full_backup_${new Date().toISOString().slice(0,10)}.${extension}`;
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading full database:', error);
      alert('Gabim në shkarkimin e database');
    }
  };

  const handleUpdateSettings = async () => {
    try {
      await api.put('/backups/settings', null, {
        params: {
          hour: newSettings.hour,
          minute: newSettings.minute,
          retention_days: newSettings.retention_days
        }
      });
      setShowSettingsModal(false);
      await fetchData();
    } catch (error) {
      console.error('Error updating settings:', error);
      alert('Gabim në përditësimin e cilësimeve');
    }
  };

  const handleRestore = async () => {
    if (!selectedBackup) return;
    
    if (!window.confirm(`KUJDES! Kjo do të mbishkruajë të gjitha të dhënat aktuale me backup "${selectedBackup.backup_name}". Vazhdoni?`)) {
      return;
    }
    
    try {
      await api.post(`/backups/${selectedBackup.id}/restore?confirm=true`);
      setShowRestoreModal(false);
      setSelectedBackup(null);
      alert('Restore u krye me sukses!');
      await fetchData();
    } catch (error) {
      console.error('Error restoring backup:', error);
      alert(error.response?.data?.detail || 'Gabim në restore');
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="backups-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Backup & Restore</h1>
          <p className="text-sm text-gray-500">Menaxho backup-et automatike dhe manuale të database</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Download Full Database Dropdown */}
          <div className="relative group">
            <button 
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition"
              data-testid="download-full-btn"
            >
              <Download className="w-4 h-4" />
              Shkarko Database
              <ChevronDown className="w-4 h-4" />
            </button>
            <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all w-56">
              <div className="py-2">
                <button
                  onClick={() => handleDownloadFullDatabase('json')}
                  className="w-full text-left px-4 py-2.5 hover:bg-gray-50 flex items-center gap-3"
                >
                  <FileArchive className="w-5 h-5 text-blue-500" />
                  <div>
                    <p className="font-medium text-gray-900">JSON Format</p>
                    <p className="text-xs text-gray-500">Një file me të gjitha të dhënat</p>
                  </div>
                </button>
                <button
                  onClick={() => handleDownloadFullDatabase('mongodump')}
                  className="w-full text-left px-4 py-2.5 hover:bg-gray-50 flex items-center gap-3"
                >
                  <Database className="w-5 h-5 text-green-500" />
                  <div>
                    <p className="font-medium text-gray-900">MongoDB Dump</p>
                    <p className="text-xs text-gray-500">Format nativ për mongorestore</p>
                  </div>
                </button>
              </div>
            </div>
          </div>
          
          <button 
            onClick={() => setShowSettingsModal(true)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition"
            data-testid="settings-btn"
          >
            <Settings className="w-4 h-4" />
            Cilësimet
          </button>
          <button 
            onClick={handleCreateBackup}
            disabled={creating}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition disabled:opacity-50"
            data-testid="create-backup-btn"
          >
            {creating ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Database className="w-4 h-4" />
            )}
            {creating ? 'Duke krijuar...' : 'Krijo Backup Tani'}
          </button>
          <button 
            onClick={fetchData}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Schedule Info Card */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center">
              <Clock className="w-7 h-7" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Backup Automatik</h2>
              <p className="text-blue-100">{settings?.schedule || 'Çdo ditë në orën 17:00'}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-blue-100 text-sm">Backup i radhës</p>
            <p className="text-2xl font-bold">
              {settings?.next_run ? formatDate(settings.next_run) : '--'}
            </p>
          </div>
        </div>
        
        <div className="mt-4 pt-4 border-t border-white/20 flex items-center gap-6">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-300" />
            <span>Scheduler {settings?.scheduler_running ? 'aktiv' : 'joaktiv'}</span>
          </div>
          <div className="flex items-center gap-2">
            <HardDrive className="w-5 h-5 text-blue-200" />
            <span>Ruajtja: {settings?.retention_days || 30} ditë</span>
          </div>
          <div className="flex items-center gap-2">
            <FileArchive className="w-5 h-5 text-blue-200" />
            <span>{backups.length} backup të ruajtura</span>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Total Backups</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{backups.length}</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center text-blue-600">
              <Database className="w-6 h-6" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Madhësia Totale</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">
                {formatSize(backups.reduce((sum, b) => sum + (b.size_bytes || 0), 0))}
              </p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center text-purple-600">
              <HardDrive className="w-6 h-6" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Backup i Fundit</p>
              <p className="text-lg font-bold text-gray-900 mt-1">
                {backups[0] ? formatDate(backups[0].created_at) : '--'}
              </p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center text-green-600">
              <CheckCircle className="w-6 h-6" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Dokumenta të Ruajtur</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">
                {backups[0]?.total_documents?.toLocaleString() || 0}
              </p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center text-orange-600">
              <FileArchive className="w-6 h-6" />
            </div>
          </div>
        </div>
      </div>

      {/* Backups Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Lista e Backup-eve</h3>
        </div>
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Emri</th>
              <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Tipi</th>
              <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Data</th>
              <th className="text-center px-6 py-3 text-xs font-medium text-gray-500 uppercase">Koleksione</th>
              <th className="text-center px-6 py-3 text-xs font-medium text-gray-500 uppercase">Dokumenta</th>
              <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">Madhësia</th>
              <th className="text-center px-6 py-3 text-xs font-medium text-gray-500 uppercase">Statusi</th>
              <th className="text-center px-6 py-3 text-xs font-medium text-gray-500 uppercase">Veprime</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="8" className="px-6 py-12 text-center">
                  <RefreshCw className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-500">Duke ngarkuar...</p>
                </td>
              </tr>
            ) : backups.length === 0 ? (
              <tr>
                <td colSpan="8" className="px-6 py-12 text-center">
                  <Database className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500">Nuk ka backup të ruajtura</p>
                  <button
                    onClick={handleCreateBackup}
                    className="mt-4 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700"
                  >
                    Krijo Backup të Parë
                  </button>
                </td>
              </tr>
            ) : (
              backups.map((backup) => (
                <tr key={backup.id} className="hover:bg-gray-50 transition">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600">
                        <Database className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{backup.backup_name}</p>
                        <p className="text-xs text-gray-500 font-mono">{backup.id.slice(0, 8)}...</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                      backup.backup_type === 'scheduled' 
                        ? 'bg-blue-100 text-blue-700' 
                        : 'bg-purple-100 text-purple-700'
                    }`}>
                      {backup.backup_type === 'scheduled' ? (
                        <Clock className="w-3 h-3" />
                      ) : (
                        <Play className="w-3 h-3" />
                      )}
                      {backup.backup_type === 'scheduled' ? 'Automatik' : 'Manual'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-sm text-gray-900">{formatDate(backup.created_at)}</p>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="text-sm text-gray-700">{backup.collections_count}</span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="text-sm font-medium text-gray-900">
                      {backup.total_documents?.toLocaleString()}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <span className="text-sm text-gray-700">{backup.size_mb} MB</span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                      backup.status === 'completed' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {backup.status === 'completed' ? (
                        <CheckCircle className="w-3 h-3" />
                      ) : (
                        <Clock className="w-3 h-3" />
                      )}
                      {backup.status === 'completed' ? 'Kompletuar' : backup.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={() => handleDownloadBackup(backup)}
                        className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg"
                        title="Shkarko"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedBackup(backup);
                          setShowRestoreModal(true);
                        }}
                        className="p-2 text-orange-500 hover:bg-orange-50 rounded-lg"
                        title="Restore"
                      >
                        <RotateCcw className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteBackup(backup.id)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                        title="Fshi"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-500 mt-0.5" />
        <div>
          <h4 className="font-medium text-blue-900">Si funksionon sistemi i backup?</h4>
          <ul className="mt-2 text-sm text-blue-700 space-y-1">
            <li>• Backup automatik ekzekutohet çdo ditë në orën e caktuar (default 17:00)</li>
            <li>• Çdo koleksion i database eksportohet në format JSON dhe kompresohet</li>
            <li>• Backup-et e vjetra fshihen automatikisht pas {settings?.retention_days || 30} ditëve</li>
            <li>• Mund të shkarkoni backup si ZIP ose ta riktheni në database</li>
          </ul>
        </div>
      </div>

      {/* Settings Modal */}
      {showSettingsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Cilësimet e Backup</h2>
              <button onClick={() => setShowSettingsModal(false)} className="p-1 hover:bg-gray-100 rounded-full">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ora e Backup Automatik
                </label>
                <div className="flex items-center gap-2">
                  <select
                    value={newSettings.hour}
                    onChange={(e) => setNewSettings({ ...newSettings, hour: parseInt(e.target.value) })}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    {Array.from({ length: 24 }, (_, i) => (
                      <option key={i} value={i}>{i.toString().padStart(2, '0')}</option>
                    ))}
                  </select>
                  <span className="text-gray-500">:</span>
                  <select
                    value={newSettings.minute}
                    onChange={(e) => setNewSettings({ ...newSettings, minute: parseInt(e.target.value) })}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value={0}>00</option>
                    <option value={15}>15</option>
                    <option value={30}>30</option>
                    <option value={45}>45</option>
                  </select>
                </div>
                <p className="text-xs text-gray-500 mt-1">Ora e serverit (UTC)</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ruajtja e Backup-eve (ditë)
                </label>
                <input
                  type="number"
                  min="7"
                  max="365"
                  value={newSettings.retention_days}
                  onChange={(e) => setNewSettings({ ...newSettings, retention_days: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
                <p className="text-xs text-gray-500 mt-1">Backup-et më të vjetra do të fshihen automatikisht</p>
              </div>
            </div>

            <div className="border-t border-gray-200 p-4 flex gap-3">
              <button
                onClick={() => setShowSettingsModal(false)}
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition"
              >
                Anulo
              </button>
              <button
                onClick={handleUpdateSettings}
                className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition"
              >
                Ruaj Ndryshimet
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Restore Modal */}
      {showRestoreModal && selectedBackup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-red-600 flex items-center gap-2">
                <AlertCircle className="w-6 h-6" />
                Konfirmo Restore
              </h2>
              <button onClick={() => { setShowRestoreModal(false); setSelectedBackup(null); }} className="p-1 hover:bg-gray-100 rounded-full">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-700 font-medium">⚠️ KUJDES!</p>
                <p className="text-red-600 text-sm mt-1">
                  Kjo veprim do të mbishkruajë TË GJITHA të dhënat aktuale me backup-in e zgjedhur. 
                  Ky veprim NUK mund të kthehet prapa!
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">Backup i zgjedhur:</p>
                <p className="font-bold text-gray-900">{selectedBackup.backup_name}</p>
                <p className="text-sm text-gray-500 mt-1">
                  {formatDate(selectedBackup.created_at)} • {selectedBackup.total_documents?.toLocaleString()} dokumenta
                </p>
              </div>
            </div>

            <div className="border-t border-gray-200 p-4 flex gap-3">
              <button
                onClick={() => { setShowRestoreModal(false); setSelectedBackup(null); }}
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition"
              >
                Anulo
              </button>
              <button
                onClick={handleRestore}
                className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition"
              >
                Po, Restore Tani
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BackupsPage;
